import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bot,
  Lock,
  MessageSquare,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import Nav from "../site/Nav";
import Footer from "../site/Footer";
import IosInstallPrompt from "../site/IosInstallPrompt";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tme_admin_token";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString()} · ${d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  } catch {
    return iso;
  }
};

export default function AdminDeliveriesPage() {
  const [token, setToken] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.sessionStorage.getItem(TOKEN_KEY) || "";
  });
  const [tokenInput, setTokenInput] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async (bearer) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/deliveries`, {
        headers: { Authorization: `Bearer ${bearer}` },
      });
      setItems(res.data?.items || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setError("Token rejected. Check ADMIN_TOKEN and try again.");
        window.sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else if (status === 503) {
        setError("Delivery Agent is offline. Configure ANTHROPIC_API_KEY.");
      } else {
        setError(err?.response?.data?.detail || "Could not load deliveries.");
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  if (!token) {
    return (
      <div
        className="bg-sand-100 text-ink min-h-screen"
        data-testid="admin-deliveries-page"
      >
        <Nav />
        <main className="pt-28 sm:pt-32 md:pt-36 pb-20 px-5 sm:px-6 md:px-10">
          <div className="max-w-md mx-auto">
            <div className="rounded-3xl border border-sand-300 bg-sand-50 p-8 sm:p-10">
              <span className="inline-flex h-11 w-11 grid place-items-center rounded-full bg-clay/10 text-clay border border-clay/30">
                <Lock size={18} />
              </span>
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
                Admin · Deliveries
              </p>
              <h1 className="font-display mt-3 text-[2rem] sm:text-[2.4rem] leading-[1.1] tracking-[-0.02em] font-semibold text-ink">
                Enter admin token
              </h1>
              <p className="mt-4 text-[14px] text-ink-muted leading-relaxed">
                Active client delivery workspaces, each running on the Modern
                Exit Delivery Agent.
              </p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!tokenInput.trim()) return;
                  window.sessionStorage.setItem(TOKEN_KEY, tokenInput.trim());
                  setToken(tokenInput.trim());
                }}
                className="mt-8 space-y-4"
              >
                <input
                  type="password"
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  placeholder="paste ADMIN_TOKEN"
                  data-testid="admin-token-input"
                  className="w-full h-12 rounded-xl border border-sand-300 bg-sand-50 px-4 text-ink placeholder:text-ink-muted/50 focus:border-clay focus:outline-none focus:ring-2 focus:ring-clay/20 transition-colors"
                />
                {error && <p className="text-[12.5px] text-clay">{error}</p>}
                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink-deep text-sand-100 px-6 py-3.5 text-[14px] font-medium hover:bg-rust transition-colors min-h-[48px]"
                >
                  Unlock
                  <ArrowRight size={14} />
                </button>
              </form>
            </div>
          </div>
        </main>
        <Footer />
        <IosInstallPrompt />
      </div>
    );
  }

  return (
    <div
      className="bg-sand-100 text-ink min-h-screen"
      data-testid="admin-deliveries-page"
    >
      <Nav />
      <main className="pt-24 sm:pt-28 md:pt-32 pb-20 px-5 sm:px-6 md:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay flex items-center gap-2">
                <ShieldCheck size={12} /> Admin · Deliveries
              </p>
              <h1 className="font-display mt-3 text-[2rem] sm:text-[2.4rem] md:text-[3rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
                Client delivery workspaces
              </h1>
              <p className="mt-3 text-[14px] text-ink-muted leading-relaxed max-w-2xl">
                Each row is a paid client running on the Modern Exit Delivery
                Agent. Open one to chat with the agent, work through stages,
                and produce delivery documents.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to="/admin/intakes"
                className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-sand-50 px-4 py-2.5 text-[12.5px] text-ink hover:border-clay/50 transition-colors min-h-[40px]"
              >
                Intakes
              </Link>
              <button
                onClick={() => load(token)}
                disabled={loading}
                data-testid="admin-refresh"
                className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-sand-50 px-4 py-2.5 text-[12.5px] text-ink hover:border-clay/50 transition-colors min-h-[40px]"
              >
                <RefreshCw
                  size={13}
                  className={loading ? "animate-spin" : ""}
                />{" "}
                Refresh
              </button>
              <button
                onClick={() => {
                  window.sessionStorage.removeItem(TOKEN_KEY);
                  setToken("");
                  setItems([]);
                }}
                data-testid="admin-logout"
                className="inline-flex items-center gap-2 rounded-full border border-sand-300 bg-sand-50 px-4 py-2.5 text-[12.5px] text-ink-muted hover:text-ink transition-colors min-h-[40px]"
              >
                Lock
              </button>
            </div>
          </div>

          {error && <p className="mt-6 text-[13px] text-clay">{error}</p>}

          <div className="mt-8 rounded-3xl border border-sand-300 bg-sand-50 overflow-hidden">
            {items.length === 0 && !loading && (
              <div className="p-10 text-center">
                <Bot size={28} className="mx-auto text-ink-muted/50" />
                <p className="mt-4 text-[14px] text-ink-muted">
                  No deliveries yet. A workspace activates automatically on
                  Stripe payment for any Revenue Systems intake.
                </p>
              </div>
            )}
            {items.map((it) => (
              <Link
                key={it.id}
                to={`/admin/deliveries/${it.id}`}
                data-testid={`delivery-row-${it.id}`}
                className="block px-4 sm:px-6 py-5 border-b border-sand-300 last:border-b-0 hover:bg-clay/[0.04] transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-medium text-ink leading-tight truncate">
                      {it.business_name || "—"}
                    </p>
                    <p className="mt-1 text-[12.5px] text-ink-muted truncate">
                      {it.client_name || "—"}
                      {it.client_email && (
                        <>
                          <span className="text-ink-muted/50"> · </span>
                          <span>{it.client_email}</span>
                        </>
                      )}
                    </p>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-olive border border-olive/30 bg-olive/10 rounded-full px-2.5 py-1 inline-flex items-center gap-1.5 shrink-0">
                    <Bot size={11} /> Active
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-5">
                  <Cell label="Package" value={it.package_label || "—"} />
                  <Cell label="Stage" value={it.current_stage || "—"} />
                  <Cell
                    label="Messages"
                    value={String(it.messages_count ?? 0)}
                    mono
                  />
                  <Cell label="Last activity" value={fmtDate(it.updated_at)} mono />
                </div>
                <div className="mt-3 flex items-center gap-2 text-[12px] text-clay">
                  <MessageSquare size={11} /> Open workspace
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <IosInstallPrompt />
    </div>
  );
}

function Cell({ label, value, mono }) {
  return (
    <div className="min-w-0">
      <p className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-ink-muted">
        {label}
      </p>
      <p
        className={`mt-1 text-[13px] text-ink leading-snug truncate ${
          mono ? "font-mono text-[12px] text-ink-muted" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}
